(() => {
  if (window.HoneFreeRotation) return;
  const rad = Math.PI / 180, states = new WeakMap(), routes = new Map(), bound = new WeakSet();
  const mul = (a,b) => [
    a[3]*b[0]+a[0]*b[3]+a[1]*b[2]-a[2]*b[1],
    a[3]*b[1]-a[0]*b[2]+a[1]*b[3]+a[2]*b[0],
    a[3]*b[2]+a[0]*b[1]-a[1]*b[0]+a[2]*b[3],
    a[3]*b[3]-a[0]*b[0]-a[1]*b[1]-a[2]*b[2]
  ];
  const unit = q => { const length=Math.hypot(...q)||1; return q.map(n=>n/length); };
  const axis = (v,a) => [...v.map(n=>n*Math.sin(a/2)),Math.cos(a/2)];
  const inverse = q => [-q[0],-q[1],-q[2],q[3]];
  const vector = (q,v) => mul(mul(q,[...v,0]),inverse(q)).slice(0,3);
  const readQuaternion = value => {
    const angles=(value||'0 0 0').trim().split(/\s+/).map(v=>(parseFloat(v)||0)*(v.endsWith('rad')?1:rad));
    const [roll=0,pitch=0,yaw=0]=angles;
    return unit(mul(axis([0,1,0],yaw),mul(axis([1,0,0],pitch),axis([0,0,1],roll))));
  };
  const orientation = q => {
    const [x,y,z,w]=q, m23=2*(y*z-x*w), pitch=Math.asin(-Math.max(-1,Math.min(1,m23)));
    const yaw=Math.abs(m23)<.9999999?Math.atan2(2*(x*z+y*w),1-2*(x*x+y*y)):Math.atan2(-2*(x*z-y*w),1-2*(y*y+z*z));
    const roll=Math.abs(m23)<.9999999?Math.atan2(2*(x*y+z*w),1-2*(x*x+z*z)):0;
    return roll+'rad '+pitch+'rad '+yaw+'rad';
  };
  function rebase(model) {
    let s=states.get(model);
    if (!s) { s={model,points:new Map(),frame:0}; states.set(model,s); }
    const orbit=model.getCameraOrbit(), target=model.getCameraTarget();
    s.q=readQuaternion(model.orientation);
    s.pivot=vector(inverse(s.q),[target.x,target.y,target.z]);
    s.orbit=[orbit.theta,orbit.phi,orbit.radius];
    const t=orbit.theta,p=orbit.phi;
    s.right=[Math.cos(t),0,-Math.sin(t)];
    s.up=[-Math.cos(p)*Math.sin(t),Math.sin(p),-Math.cos(p)*Math.cos(t)];
    s.front=[Math.sin(p)*Math.sin(t),Math.cos(p),Math.sin(p)*Math.cos(t)];
    if(!s.baseRadius) s.baseRadius=orbit.radius;
    return s;
  }
  function commit(s) {
    if(s.frame) return;
    s.frame=requestAnimationFrame(async()=>{
      s.frame=0;
      const m=s.model;
      if(!m.isConnected||!m.loaded) return;
      m.orientation=orientation(s.q);
      m.cameraTarget=vector(s.q,s.pivot).map(v=>v+'m').join(' ');
      m.cameraOrbit=s.orbit[0]+'rad '+s.orbit[1]+'rad '+s.orbit[2]+'m';
      await m.updateComplete;
      if(m.isConnected) m.jumpCameraToGoal();
    });
  }
  function turn(s,dx,dy,roll=0) {
    s.q=unit(mul(axis(s.front,roll),mul(axis(s.right,dy),mul(axis(s.up,dx),s.q))));
    commit(s);
  }
  function zoom(s,factor) {
    s.orbit[2]=Math.max(s.baseRadius*.25,Math.min(s.baseRadius*3,s.orbit[2]*factor));
    commit(s);
  }
  function resolve(event) {
    const path=event.composedPath();
    let surface=path.find(n=>n instanceof Element&&n.matches('.hone360-gesture'));
    const model=surface?surface.closest('hone-product-360')?.querySelector('model-viewer'):path.find(n=>n instanceof Element&&n.tagName==='MODEL-VIEWER');
    if(!model?.loaded||typeof model.getCameraOrbit!=='function') return null;
    return {model,surface:surface||model};
  }
  const stop=e=>{e.preventDefault();e.stopImmediatePropagation();};
  const distance=s=>{const p=[...s.points.values()];return p.length===2?Math.hypot(p[1][0]-p[0][0],p[1][1]-p[0][1]):0;};
  document.addEventListener('pointerdown',e=>{
    const hit=resolve(e);
    if(!hit||(e.pointerType==='mouse'&&e.button!==0)) return;
    stop(e);
    let s=states.get(hit.model);
    if(!s?.points.size) s=rebase(hit.model);
    s.surface=hit.surface;
    s.surface.focus({preventScroll:true});
    s.surface.setPointerCapture(e.pointerId);
    s.points.set(e.pointerId,[e.clientX,e.clientY]); routes.set(e.pointerId,s); s.distance=distance(s);
    s.surface.style.cursor='grabbing';
  },{capture:true,passive:false});
  document.addEventListener('pointermove',e=>{
    const s=routes.get(e.pointerId); if(!s) return;
    stop(e);
    const old=s.points.get(e.pointerId); s.points.set(e.pointerId,[e.clientX,e.clientY]);
    if(s.points.size===2){const next=distance(s);if(s.distance&&next)zoom(s,s.distance/next);s.distance=next;}
    else if(s.points.size===1)turn(s,(e.clientX-old[0])*.009,(e.clientY-old[1])*.009);
  },{capture:true,passive:false});
  const release=e=>{
    const s=routes.get(e.pointerId);if(!s)return;
    stop(e);s.points.delete(e.pointerId);routes.delete(e.pointerId);s.distance=distance(s);
    if(!s.points.size)s.surface.style.cursor='grab';
    if(s.surface.hasPointerCapture(e.pointerId))s.surface.releasePointerCapture(e.pointerId);
  };
  ['pointerup','pointercancel','lostpointercapture'].forEach(type=>document.addEventListener(type,release,{capture:true,passive:false}));
  document.addEventListener('wheel',e=>{
    const hit=resolve(e);if(!hit)return;stop(e);
    zoom(rebase(hit.model),Math.exp(Math.max(-150,Math.min(150,e.deltaY))*.003));
  },{capture:true,passive:false});
  document.addEventListener('keydown',e=>{
    const hit=resolve(e);if(!hit)return;
    const moves={ArrowLeft:[-.13,0],ArrowRight:[.13,0],ArrowUp:[0,-.13],ArrowDown:[0,.13]};
    if(moves[e.key]){stop(e);turn(rebase(hit.model),...moves[e.key]);}
    else if(e.key==='+'||e.key==='='){stop(e);zoom(rebase(hit.model),.8);}
    else if(e.key==='-'){stop(e);zoom(rebase(hit.model),1.25);}
  },{capture:true,passive:false});
  function bind(model) {
    if(bound.has(model))return;bound.add(model);
    model.setAttribute('touch-action','none');
    model.style.touchAction='none';model.style.cursor='grab';
  }
  function scan(node) {
    if(!(node instanceof Element))return;
    if(node.matches('model-viewer'))bind(node);
    node.querySelectorAll('model-viewer').forEach(bind);
  }
  window.HoneFreeRotation={rotate(model,horizontal=0,vertical=0,roll=0){
    if(model?.loaded)turn(rebase(model),horizontal*rad,vertical*rad,roll*rad);
  }};
  const start=()=>{
    scan(document.documentElement);
    new MutationObserver(records=>records.forEach(r=>r.addedNodes.forEach(scan))).observe(document.documentElement,{childList:true,subtree:true});
  };
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',start,{once:true});else start();
})();